import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { messagesAPI } from '../../services/api';

interface UserPublic {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  country: string | null;
  trust_rating: number;
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  sender?: UserPublic;
}

interface Conversation {
  id: string;
  other_user: UserPublic;
  last_message: Message | null;
  unread_count: number;
  last_message_at: string | null;
}

interface MessagesState {
  conversations: Conversation[];
  currentMessages: Message[];
  activeConversationId: string | null;
  loading: boolean;
  messagesLoading: boolean;
  unreadCount: number;
  conversationsTotal: number;
  messagesTotal: number;
}

const initialState: MessagesState = {
  conversations: [],
  currentMessages: [],
  activeConversationId: null,
  loading: false,
  messagesLoading: false,
  unreadCount: 0,
  conversationsTotal: 0,
  messagesTotal: 0,
};

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getConversations(params.page, params.limit);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch conversations');
    }
  }
);

export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (params: { conversationId: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getMessages(params.conversationId, params.page, params.limit);
      return { ...response.data, page: params.page || 1 };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (params: { conversationId: string; content: string }, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.sendMessage(params.conversationId, params.content);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send message');
    }
  }
);

export const startConversation = createAsyncThunk(
  'messages/startConversation',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.startConversation(userId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to start conversation');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'messages/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getUnreadCount();
      return response.data.unread_count;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch unread count');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'messages/markAsRead',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await messagesAPI.markAsRead(conversationId);
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark as read');
    }
  }
);

export const refreshMessages = createAsyncThunk(
  'messages/refreshMessages',
  async (params: { conversationId: string }, { rejectWithValue }) => {
    try {
      const response = await messagesAPI.getMessages(params.conversationId, 1, 30);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to refresh messages');
    }
  }
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    setActiveConversation: (state, action: PayloadAction<string | null>) => {
      state.activeConversationId = action.payload;
    },
    wsNewMessage: (state, action: PayloadAction<{ conversation_id: string; message: Message }>) => {
      const { conversation_id, message } = action.payload;

      // Add to current messages if viewing this conversation
      if (state.activeConversationId === conversation_id) {
        if (!state.currentMessages.some((m) => m.id === message.id)) {
          state.currentMessages.push(message);
        }
      }

      // Update conversation list
      const conv = state.conversations.find((c) => c.id === conversation_id);
      if (conv) {
        conv.last_message = message;
        conv.last_message_at = message.created_at;
        if (state.activeConversationId !== conversation_id) {
          conv.unread_count += 1;
        }
      }

      // Only increment unread if not currently viewing this conversation
      if (state.activeConversationId !== conversation_id) {
        state.unreadCount += 1;
      }
    },
    wsMessagesRead: (state, action: PayloadAction<{ conversation_id: string; reader_id: string }>) => {
      const { conversation_id } = action.payload;

      // Mark messages as read in current view
      state.currentMessages.forEach((msg) => {
        if (msg.conversation_id === conversation_id) {
          msg.is_read = true;
        }
      });
    },
    clearCurrentMessages: (state) => {
      state.currentMessages = [];
      state.messagesTotal = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.conversations || [];
        state.conversationsTotal = action.payload.total;
      })
      .addCase(fetchConversations.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const messages = action.payload.messages || [];
        if (action.payload.page === 1) {
          state.currentMessages = messages.reverse();
        } else {
          // Prepend older messages
          const reversed = messages.reverse();
          state.currentMessages = [...reversed, ...state.currentMessages];
        }
        state.messagesTotal = action.payload.total;
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.messagesLoading = false;
      })
      .addCase(sendMessage.fulfilled, (state, action: PayloadAction<Message>) => {
        // Add to current messages
        if (!state.currentMessages.some((m) => m.id === action.payload.id)) {
          state.currentMessages.push(action.payload);
        }

        // Update conversation list
        const conv = state.conversations.find((c) => c.id === action.payload.conversation_id);
        if (conv) {
          conv.last_message = action.payload;
          conv.last_message_at = action.payload.created_at;
        }
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action: PayloadAction<number>) => {
        state.unreadCount = action.payload;
      })
      .addCase(markAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        const conv = state.conversations.find((c) => c.id === action.payload);
        if (conv) {
          state.unreadCount = Math.max(0, state.unreadCount - conv.unread_count);
          conv.unread_count = 0;
        }
      })
      .addCase(refreshMessages.fulfilled, (state, action) => {
        const messages = (action.payload.messages || []).reverse();
        const existingIds = new Set(state.currentMessages.map((m) => m.id));
        const newMessages = messages.filter((m: Message) => !existingIds.has(m.id));
        if (newMessages.length > 0) {
          state.currentMessages.push(...newMessages);
        }
        state.messagesTotal = action.payload.total;
      });
  },
});

export const { setActiveConversation, wsNewMessage, wsMessagesRead, clearCurrentMessages } = messagesSlice.actions;
export default messagesSlice.reducer;
